// ./src/components/features/VolunteerApplications/VolunteerApplicationsList/VolunteerApplicationsList.tsx
//
// The volunteer sign-ups on one event (#A4.6) — an inbox, not a CRUD list. A host reads a row and decides.
//
// ⚠ DECISIONS ARE PER SHIFT. The row's quick Approve / Decline act on the shifts still WAITING; anything finer
// (one of three shifts, withdrawing a confirmed one) happens in the drawer.
//
// Presentation per the house rule: desktop (≥ md) a <Table>, tablet cards with buttons, phone a <SwipeableCard>.
import React from "react";
import { Table, Paper, Stack, Group, Text, Badge, ActionIcon, Tooltip, Skeleton, Anchor } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  AppIcon,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  CardListSkeleton,
  EmptyState,
  LoadMoreButton,
  TableDateTime,
} from "@/components/generic";
import { useAppEnvironment } from "@/contexts";
import { HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  VolunteerApplication,
  VOLUNTEER_DECISION,
  VOLUNTEER_SHIFT_STATUS,
  VOLUNTEER_SHIFT_STATUS_COLORS,
  pendingShiftCount,
  volunteerName,
  volunteerShiftStatusLabel,
} from "@/utils/api";

interface VolunteerApplicationsListProps {
  applications: VolunteerApplication[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onOpen: (application: VolunteerApplication) => void;
  /** Approve every WAITING shift. */
  onApprove: (application: VolunteerApplication) => void;
  /** Decline every WAITING shift. */
  onDecline: (application: VolunteerApplication) => void;
  isDeciding: (id?: string, decision?: string) => boolean;
  highlightedId?: string | null;
  hasQueryNarrowing?: boolean;
}

/** The shifts at a glance: one badge per state that has any ("2 waiting", "1 approved"). */
const ShiftsCell: React.FC<{ application: VolunteerApplication }> = ({ application }) => {
  const counts = new Map<string, number>();

  for (const s of application.shifts ?? []) {
    const k = String(s.applicationStatus || "");
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }

  const order = [VOLUNTEER_SHIFT_STATUS.PENDING, VOLUNTEER_SHIFT_STATUS.APPROVED, VOLUNTEER_SHIFT_STATUS.DECLINED, VOLUNTEER_SHIFT_STATUS.WITHDRAWN];

  return (
    <Group gap={4} wrap="wrap">
      {order
        .filter((k) => (counts.get(k) ?? 0) > 0)
        .map((k) => (
          <Badge key={k} size="md" variant="light" color={VOLUNTEER_SHIFT_STATUS_COLORS[k] || "gray"}>
            {`${counts.get(k)} ${volunteerShiftStatusLabel(k).toLowerCase()}`}
          </Badge>
        ))}
    </Group>
  );
};

const RowActions: React.FC<{
  application: VolunteerApplication;
  onOpen: (a: VolunteerApplication) => void;
  onApprove: (a: VolunteerApplication) => void;
  onDecline: (a: VolunteerApplication) => void;
  isDeciding: (id?: string, decision?: string) => boolean;
}> = ({ application, onOpen, onApprove, onDecline, isDeciding }) => {
  const { t } = useTranslation(["volunteers", "common"]);
  const approving = isDeciding(application.id, VOLUNTEER_DECISION.APPROVE);
  const declining = isDeciding(application.id, VOLUNTEER_DECISION.DECLINE);
  const busy = isDeciding(application.id);
  // ⚠ Quick decisions exist only while something waits — gone, not disabled, once nothing does.
  const waiting = pendingShiftCount(application) > 0;

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      <Tooltip label={t("volunteers:applications.actions.open", { defaultValue: "Open" })} withArrow position="bottom">
        <ActionIcon
          variant="subtle"
          color="blue"
          size="md"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(application);
          }}
        >
          <AppIcon iconName="Eye" size={16} />
        </ActionIcon>
      </Tooltip>
      {waiting ? (
        <>
          <Tooltip label={t("volunteers:applications.actions.approveWaiting", { defaultValue: "Approve waiting shifts" })} withArrow position="bottom">
            <ActionIcon
              variant="subtle"
              color="green"
              size="md"
              loading={approving}
              disabled={busy && !approving}
              onClick={(e) => {
                e.stopPropagation();
                onApprove(application);
              }}
            >
              <AppIcon iconName="Check" size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t("volunteers:applications.actions.declineWaiting", { defaultValue: "Decline waiting shifts" })} withArrow position="bottom">
            <ActionIcon
              variant="subtle"
              color="red"
              size="md"
              loading={declining}
              disabled={busy && !declining}
              onClick={(e) => {
                e.stopPropagation();
                onDecline(application);
              }}
            >
              <AppIcon iconName="X" size={16} />
            </ActionIcon>
          </Tooltip>
        </>
      ) : null}
    </Group>
  );
};

/** ⚠ Matches the real row cell for cell. */
const RowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td>
      <Skeleton height={16} width="55%" radius="sm" />
      <Skeleton height={12} width="40%" radius="sm" mt={6} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={20} width={160} radius="xl" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={90} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Group gap={4} wrap="nowrap" justify="flex-end">
        <Skeleton height={28} circle />
        <Skeleton height={28} circle />
        <Skeleton height={28} circle />
      </Group>
    </Table.Td>
  </Table.Tr>
);

const ApplicationCard: React.FC<{
  application: VolunteerApplication;
  highlighted: boolean;
  showActions: boolean;
  onOpen: (a: VolunteerApplication) => void;
  onApprove: (a: VolunteerApplication) => void;
  onDecline: (a: VolunteerApplication) => void;
  isDeciding: (id?: string, decision?: string) => boolean;
}> = ({ application, highlighted, showActions, onOpen, onApprove, onDecline, isDeciding }) => {
  const { t } = useTranslation(["volunteers"]);

  return (
    <Paper withBorder radius="md" p="md" style={highlighted ? HIGHLIGHT_STYLE : undefined}>
      <Stack gap="sm">
        <Stack gap={3}>
          <Text size="md" fw={600} style={{ overflowWrap: "anywhere" }}>
            {volunteerName(application)}
          </Text>
          {application.email ? (
            <Anchor href={`mailto:${application.email}`} size="md" onClick={(e) => e.stopPropagation()}>
              {application.email}
            </Anchor>
          ) : null}
        </Stack>
        <ShiftsCell application={application} />
        <Stack gap={2}>
          <Text size="sm" c="dimmed">
            {t("volunteers:applications.submitted", { defaultValue: "Signed up" })}
          </Text>
          <TableDateTime value={application.submittedAt} inline />
        </Stack>
        {showActions ? <RowActions application={application} onOpen={onOpen} onApprove={onApprove} onDecline={onDecline} isDeciding={isDeciding} /> : null}
      </Stack>
    </Paper>
  );
};

const VolunteerApplicationsListComponent: React.FC<VolunteerApplicationsListProps> = ({
  applications,
  loading,
  isLoaded,
  loadingMore,
  hasMore,
  onLoadMore,
  onOpen,
  onApprove,
  onDecline,
  isDeciding,
  highlightedId,
  hasQueryNarrowing,
}) => {
  const { t } = useTranslation(["volunteers", "common"]);
  const { isDesktop, isMobile } = useAppEnvironment();
  const { closeAllCards, shouldForceClose, handleForceCloseComplete, openCard } = useSwipeableList();
  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && applications.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        {hasQueryNarrowing ? (
          <EmptyState
            title={t("volunteers:applications.empty.noMatchTitle", { defaultValue: "No sign-ups match" })}
            description={t("volunteers:applications.empty.noMatchDescription", { defaultValue: "Try a different search, or show every sign-up." })}
            iconName="Search"
            size="md"
          />
        ) : (
          <EmptyState
            title={t("volunteers:applications.empty.title", { defaultValue: "No volunteer sign-ups yet" })}
            description={t("volunteers:applications.empty.description", {
              defaultValue: "People sign up from your event website. Open shifts for sign-up on Volunteers & staff › Shifts and add the Volunteer shifts block to a page.",
            })}
            iconName="Inbox"
            size="md"
          />
        )}
      </Paper>
    );
  }

  const loadMore = <LoadMoreButton hasMore={!!hasMore} loading={!!loading} loadingMore={!!loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("volunteers:applications.columns.volunteer", { defaultValue: "Volunteer" })}</Table.Th>
                <Table.Th>{t("volunteers:applications.columns.shifts", { defaultValue: "Shifts" })}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t("volunteers:applications.columns.signedUp", { defaultValue: "Signed up" })}</Table.Th>
                <Table.Th style={{ width: 130 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? [0, 1, 2].map((i) => <RowSkeleton key={i} />)
                : applications.map((application) => (
                    <Table.Tr
                      key={application.id}
                      style={{
                        backgroundColor: application.id === highlightedId ? HIGHLIGHT_ROW_BG : undefined,
                        transition: "background-color 150ms ease",
                      }}
                    >
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="md" fw={600}>
                            {volunteerName(application)}
                          </Text>
                          {application.email ? (
                            <Anchor href={`mailto:${application.email}`} size="md">
                              {application.email}
                            </Anchor>
                          ) : null}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <ShiftsCell application={application} />
                      </Table.Td>
                      <Table.Td>
                        <TableDateTime value={application.submittedAt} />
                      </Table.Td>
                      <Table.Td>
                        <RowActions application={application} onOpen={onOpen} onApprove={onApprove} onDecline={onDecline} isDeciding={isDeciding} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return <CardListSkeleton rows={3} actions={3} actionsAt="bottom" lines={5} lineSizes={['md', 'md', 'xs', 'sm', 'xs']} lineGap={7} />;
  }

  if (!isMobile) {
    return (
      <Stack gap="md">
        <Stack gap="sm">
          {applications.map((application) => (
            <ApplicationCard
              key={application.id}
              application={application}
              highlighted={application.id === highlightedId}
              showActions
              onOpen={onOpen}
              onApprove={onApprove}
              onDecline={onDecline}
              isDeciding={isDeciding}
            />
          ))}
        </Stack>
        {loadMore}
      </Stack>
    );
  }

  // Phone: the swipe IS the actions — approve / decline ("do something") left, Open (identity) right.
  return (
    <Stack gap="md">
      <Stack gap="sm">
        {applications.map((application) => {
          const id = application.id || "";
          const waiting = pendingShiftCount(application) > 0;
          const actions: SwipeAction[] = [
            ...(waiting
              ? [
                  {
                    key: "approve",
                    label: t("volunteers:applications.actions.approve", { defaultValue: "Approve" }),
                    icon: "Check",
                    color: "green",
                    secondary: true,
                    loading: isDeciding(id, VOLUNTEER_DECISION.APPROVE),
                    onClick: () => {
                      closeAllCards();
                      onApprove(application);
                    },
                  },
                  {
                    key: "decline",
                    label: t("volunteers:applications.actions.decline", { defaultValue: "Decline" }),
                    icon: "X",
                    color: "red",
                    secondary: true,
                    loading: isDeciding(id, VOLUNTEER_DECISION.DECLINE),
                    onClick: () => {
                      closeAllCards();
                      onDecline(application);
                    },
                  },
                ]
              : []),
            {
              key: "open",
              label: t("volunteers:applications.actions.open", { defaultValue: "Open" }),
              icon: "Eye",
              color: "blue",
              onClick: () => {
                closeAllCards();
                onOpen(application);
              },
            },
          ];

          return (
            <SwipeableCard
              key={id}
              leftSwipeActions={actions}
              forceClose={shouldForceClose(id)}
              onForceCloseComplete={() => handleForceCloseComplete(id)}
              onSwipeStateChange={(isOpen) => {
                if (isOpen) {
                  openCard(id);
                }
              }}
              borderRadius="var(--mantine-radius-md)"
            >
              <ApplicationCard
                application={application}
                highlighted={application.id === highlightedId}
                showActions={false}
                onOpen={onOpen}
                onApprove={onApprove}
                onDecline={onDecline}
                isDeciding={isDeciding}
              />
            </SwipeableCard>
          );
        })}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const VolunteerApplicationsList = React.memo(VolunteerApplicationsListComponent);
